import type { DesignAssetView } from '@repo/api-contracts';
import { useMemo } from 'react';
import { trpc } from '@/shared/api';
import { chunkAssetIds } from '../model/chunk-asset-ids';

/** 표시 URL 수명(서버 VIEW_URL_EXPIRES_IN_SECONDS 와 같은 3600초). 0.8 배 주기로 다시 받는다(P6-D12). */
const URL_TTL_SECONDS = 3600;
const REFRESH_RATIO = 0.8;
export const ASSET_URL_REFRESH_MS = URL_TTL_SECONDS * REFRESH_RATIO * 1000;

interface DesignAssetUrlsOptions {
  readonly enabled: boolean;
  /** 비회원 키(G6). 있으면 비회원 API(designs.guestAssets.resolveUrls)로 묻는다 — 없거나 null 이면 회원 API. */
  readonly guestKey?: string | null;
}

export interface DesignAssetUrls {
  readonly urls: ReadonlyMap<string, DesignAssetView>;
  readonly isPending: boolean;
  /** 이미지 로드가 403 으로 실패했을 때 한 번 다시 받는다. */
  readonly refresh: () => Promise<void>;
}

/**
 * 문서의 이미지 에셋 표시 URL. 100개씩 나눠 묻고 결과를 assetId 맵으로 합친다.
 * 회원은 designs.assets.resolveUrls, 비회원은 비회원 키로 designs.guestAssets.resolveUrls — 한쪽만 조각을 만든다.
 */
export const useDesignAssetUrls = (
  assetIds: readonly string[],
  options: DesignAssetUrlsOptions,
): DesignAssetUrls => {
  const guestKey = options.guestKey ?? null;
  const chunks = useMemo(
    () => (options.enabled ? chunkAssetIds(assetIds) : []),
    [assetIds, options.enabled],
  );
  const queryOptions = {
    enabled: options.enabled,
    refetchInterval: ASSET_URL_REFRESH_MS,
    staleTime: ASSET_URL_REFRESH_MS,
    retry: false,
  } as const;
  const utils = trpc.useUtils();
  const memberResults = trpc.useQueries((t) =>
    guestKey === null
      ? chunks.map((ids) => t.designs.assets.resolveUrls({ assetIds: ids }, queryOptions))
      : [],
  );
  const guestResults = trpc.useQueries((t) =>
    guestKey === null
      ? []
      : chunks.map((ids) =>
          t.designs.guestAssets.resolveUrls({ guestKey, assetIds: ids }, queryOptions),
        ),
  );
  const urls = useMemo(() => {
    const map = new Map<string, DesignAssetView>();
    for (const result of [...memberResults, ...guestResults])
      for (const asset of result.data?.assets ?? []) map.set(asset.id, asset);
    return map;
  }, [memberResults, guestResults]);
  return {
    urls,
    isPending: [...memberResults, ...guestResults].some((result) => result.isPending),
    refresh: async () => {
      if (guestKey === null) await utils.designs.assets.resolveUrls.refetch();
      else await utils.designs.guestAssets.resolveUrls.refetch();
    },
  };
};
